import { useEffect, useState } from 'react';

/**
 * Detects logos that are technically valid images but have nothing in them.
 *
 * Brandfetch serves a fully transparent placeholder for brands it has no asset
 * for, responding 200 with a real `image/webp`. `onError` never fires and the
 * browser renders an invisible, correctly-sized box, so the only way to tell is
 * to draw the image and look at its alpha channel.
 *
 * The image is downscaled to a small square before scanning, which keeps the
 * check to ~1k pixel reads. The opaque-ratio floor absorbs the stray
 * anti-aliased pixels that downscaling can smear into an otherwise empty image,
 * while sitting far below the coverage of any real logo.
 *
 * Anything we cannot inspect — a decode failure, a canvas tainted by a missing
 * CORS header, an unavailable 2D context — counts as `'painted'`, so a real
 * logo is never swapped out because the check itself failed. The check only
 * runs in the browser, leaving the server render as `'checking'`.
 */
export type BlankImageState = 'checking' | 'blank' | 'painted';

const SAMPLE_SIZE = 32;
const ALPHA_THRESHOLD = 10;
const MIN_OPAQUE_RATIO = 0.002;

export function hasPaintedSample(
  pixels: Uint8ClampedArray,
  sampleSize = SAMPLE_SIZE,
): boolean {
  let opaque = 0;
  for (let offset = 3; offset < pixels.length; offset += 4) {
    if (pixels[offset] > ALPHA_THRESHOLD) opaque++;
  }

  return opaque / (sampleSize * sampleSize) > MIN_OPAQUE_RATIO;
}

function hasPaintedPixels(image: HTMLImageElement): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return true;

  context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  let pixels: Uint8ClampedArray;
  try {
    pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
  } catch {
    return true;
  }

  return hasPaintedSample(pixels);
}

export function useIsBlankImage(src: string): BlankImageState {
  const [state, setState] = useState<BlankImageState>('checking');

  useEffect(() => {
    setState('checking');

    if (!src) {
      setState('blank');
      return;
    }

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      if (cancelled) return;
      const painted =
        image.naturalWidth === 0 ||
        image.naturalHeight === 0 ||
        hasPaintedPixels(image);
      setState(painted ? 'painted' : 'blank');
    };

    image.onerror = () => {
      if (!cancelled) setState('blank');
    };

    image.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  return state;
}
